import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  alpha,
  Divider,
  Avatar,
  Badge,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Search,
  FilterList,
  CalendarToday,
  AccessTime,
  Person,
  VideoCall,
  LocationOn,
  MoreVert,
  CheckCircle,
  Pending,
  Cancel,
  Check,
  Close,
  Refresh,
  Add,
  Edit,
  Delete,
  Phone,
  Email,
  Chat,
  Assessment,
  History,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { 
  fetchAppointments, 
  fetchAppointmentStats,
  confirmAppointment, 
  cancelAppointment,
  clearError 
} from "../../features/appointments/appointmentsSlice";
import AppointmentPopup from "../../components/DoctorDashboard/AppointmentDetailsPopup";
import AppointmentActions from "../../components/AppointmentActions";
import AppointmentStatusManager from "../../components/AppointmentStatusManager";
import debounce from "lodash.debounce";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { styled } from "@mui/material/styles";

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(8px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === "Completed" ? alpha(theme.palette.success.main, 0.1) :
                  status === "Pending" ? alpha(theme.palette.warning.main, 0.1) :
                  status === "Cancelled" ? alpha(theme.palette.error.main, 0.1) :
                  alpha(theme.palette.info.main, 0.1),
  color: status === "Completed" ? theme.palette.success.main :
         status === "Pending" ? theme.palette.warning.main :
         status === "Cancelled" ? theme.palette.error.main :
         theme.palette.info.main,
  fontWeight: 500,
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

const DoctorAppointments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appointmentsState = useSelector(state => state.appointments);
  const appointments = appointmentsState?.data || appointmentsState?.results || [];
  const loading = appointmentsState?.loading || false;
  const error = appointmentsState?.error || null;
  const stats = appointmentsState?.stats || null;
  const count = appointmentsState?.count || 0;
  
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showFilters, setShowFilters] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadingStats, setLoadingStats] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");

  // Memoized stats values - 100% from backend, no client-side calculation
  // Stats represent overall totals, not filtered
  const statsValues = React.useMemo(() => {
    if (!stats) {
      console.warn('⚠️ Stats object is null/undefined - displaying zeros');
      return {
        today: 0,
        pending: 0,
        completed: 0,
        cancelled: 0,
        total: 0
      };
    }
    
    // Debug: Log the raw stats object (safely)
    try {
      console.log('📊 Raw stats object from backend:', JSON.stringify(stats, null, 2));
    } catch (e) {
      console.log('📊 Raw stats object from backend (non-serializable):', stats);
    }
    
    // Extract stats from backend response
    // Backend AppointmentStatsView returns: { total, today, by_status: { Pending: X, Completed: Y, Canceled: Z } }
    let pending = 0;
    let completed = 0;
    let cancelled = 0;
    let today = 0;
    let total = 0;
    
    // Extract from by_status if it exists
    if (stats.by_status) {
      pending = stats.by_status.Pending || stats.by_status.pending || 0;
      completed = stats.by_status.Completed || stats.by_status.completed || 0;
      const canceled = stats.by_status.Canceled || stats.by_status.canceled || 0;
      const cancelledVal = stats.by_status.Cancelled || stats.by_status.cancelled || 0;
      cancelled = canceled + cancelledVal;
      today = stats.today || 0;
      total = stats.total || 0;
    } else {
      // Fallback to direct fields (DashboardStatsView format)
      pending = stats.pending || 0;
      completed = stats.completed || 0;
      cancelled = (stats.canceled || 0) + (stats.cancelled || 0);
      today = typeof stats.today === 'object' ? (stats.today?.count || 0) : (stats.today || 0);
      total = stats.total || (pending + completed + cancelled + (stats.confirmed || 0));
    }
    
    // Log extracted values
    console.log('📊 Extracted stats values:', {
      today,
      pending,
      completed,
      cancelled,
      total,
      hasByStatus: !!stats.by_status,
      byStatusKeys: stats.by_status ? Object.keys(stats.by_status) : null
    });
    
    // Warn if all values are the same (potential bug)
    if (today === pending && pending === completed && completed === cancelled && cancelled > 0) {
      console.error('❌ All stats have the same value! This indicates a bug:', {
        today,
        pending,
        completed,
        cancelled,
        rawStats: stats
      });
    }
    
    return { today, pending, completed, cancelled, total };
  }, [stats]);
  
  // Stats are overall totals - no consistency check needed

  // Fetch appointments with retry logic
  const fetchAppointmentsWithRetry = useCallback(async (retries = 3, resetPage = false) => {
    try {
      const currentPage = resetPage ? 1 : page;
      const params = { 
        page: currentPage, 
        search: searchTerm || undefined, 
        status: filterStatus !== 'All' ? filterStatus : undefined,
        page_size: itemsPerPage 
      };
      
      const result = await dispatch(fetchAppointments(params)).unwrap();
      if (resetPage && page !== 1) {
        setPage(1);
      }
      setRetryCount(0);
      return result;
    } catch (err) {
      console.error('❌ Error fetching appointments:', err);
      if (retries > 0 && (!err?.response || err?.response?.status >= 500)) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchAppointmentsWithRetry(retries - 1, resetPage), 2000);
      } else {
        setSnackbar({
          open: true,
          message: typeof err === 'string' ? err : err?.detail || err?.message || 'Failed to load appointments. Please try again.',
          severity: 'error'
        });
      }
      throw err;
    }
  }, [dispatch, page, searchTerm, filterStatus, itemsPerPage]);

  // Fetch stats - NO FILTERS, just overall totals
  const fetchStats = useCallback(async () => {
    try {
      setLoadingStats(true);
      console.log('🔄 Fetching stats...');
      // No filters - stats are overall totals only
      const result = await dispatch(fetchAppointmentStats({})).unwrap();
      console.log('✅ Stats fetched successfully - FULL RESPONSE:', result);
      try {
        console.log('✅ Stats parsed:', {
          total: result?.total,
          today: result?.today,
          by_status: result?.by_status,
          pending: result?.by_status?.Pending || result?.by_status?.pending || 0,
          completed: result?.by_status?.Completed || result?.by_status?.completed || 0,
          cancelled: (result?.by_status?.Canceled || 0) + (result?.by_status?.Cancelled || 0),
        });
      } catch (e) {
        console.error('Error parsing stats:', e);
      }
    } catch (err) {
      // Safely log error details without causing rendering issues
      console.error('❌ Error fetching appointment stats:', err);
      try {
        const errorDetails = {
          message: err?.message || String(err),
          response: err?.response?.data ? (typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)) : undefined,
          status: err?.response?.status,
        };
        console.error('❌ Error details:', errorDetails);
      } catch (e) {
        console.error('❌ Error parsing error details:', e);
      }
      // Don't show error toast for stats - it's not critical
      // Stats failure shouldn't crash the component
    } finally {
      setLoadingStats(false);
    }
  }, [dispatch]);

  // Debounced search handler
  const debouncedSearchHandler = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      setPage(1); // Reset to page 1 on search
    }, 500),
    []
  );

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearchHandler(value);
  }, [debouncedSearchHandler]);

  // Handle status filter change
  const handleStatusFilterChange = useCallback((e) => {
    const value = e.target.value;
    setFilterStatus(value);
    setPage(1); // Reset to page 1 on filter change
  }, []);

  // Track if this is the initial mount to avoid duplicate fetches
  const isInitialMount = React.useRef(true);

  // Initial data fetch on mount
  useEffect(() => {
    if (isInitialMount.current) {
      fetchAppointmentsWithRetry(false, false);
      fetchStats(); // Fetch stats on mount
      isInitialMount.current = false;
    }
  }, []); // Run only on mount

  // Fetch appointments when page changes - stats are NOT refetched (they're independent of pagination)
  useEffect(() => {
    if (!isInitialMount.current) { // Only refetch if not initial mount (to avoid duplicate)
      fetchAppointmentsWithRetry(false, false);
    }
  }, [page]); // Only refetch appointments when page changes, NOT stats

  // Refetch when search term or filter changes (debounced)
  // Stats are NOT refetched - they're overall totals, not filter-dependent
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchAppointmentsWithRetry(false, true); // Reset page on filter/search change
      // Don't refetch stats - they're overall totals, not filter-dependent
    }, 600); // Slightly longer than debounce to ensure search term is updated
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterStatus]); // Refetch appointments when search or filter changes

  // Refresh stats periodically (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchStats();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const handleOpenPopup = useCallback((appointment) => {
    setSelectedAppointment(appointment);
    setOpenPopup(true);
  }, []);

  const handleClosePopup = useCallback(() => {
    setOpenPopup(false);
    setSelectedAppointment(null);
  }, []);

  // Refresh data after actions
  const refreshData = useCallback(async () => {
    await Promise.all([
      fetchAppointmentsWithRetry(false, false),
      fetchStats()
    ]);
  }, [fetchAppointmentsWithRetry, fetchStats]);

  const handleConfirmAppointment = useCallback(async (appointmentId) => {
    try {
      await dispatch(confirmAppointment(appointmentId)).unwrap();
      setSnackbar({
        open: true,
        message: 'Appointment confirmed successfully',
        severity: 'success'
      });
      // Refresh appointments and stats
      await refreshData();
    } catch (error) {
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.detail || error?.message || 'Failed to confirm appointment';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  }, [dispatch, refreshData]);

  const handleCancelAppointment = useCallback(async (appointmentId) => {
    try {
      await dispatch(cancelAppointment({ id: appointmentId, reason: 'Cancelled by doctor' })).unwrap();
      setSnackbar({
        open: true,
        message: 'Appointment cancelled successfully',
        severity: 'success'
      });
      // Refresh appointments and stats
      await refreshData();
    } catch (error) {
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.detail || error?.message || 'Failed to cancel appointment';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  }, [dispatch, refreshData]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <CheckCircle />;
      case "Pending":
        return <Pending />;
      case "Cancelled":
        return <Cancel />;
      default:
        return <Check />;
    }
  };

  const getConsultationTypeIcon = (type) => {
    switch (type) {
      case "Video Call":
        return <VideoCall />;
      case "In-Person":
        return <LocationOn />;
      default:
        return <Assessment />;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Appointments
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/doctor-dashboard/appointments/new')}
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}
          >
            New Appointment
          </Button>
          <Button
            variant="outlined"
            startIcon={<Search />}
            onClick={() => navigate('/doctor-dashboard/appointments/search')}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            startIcon={<History />}
            onClick={() => navigate('/doctor-dashboard/appointments/history')}
          >
            History
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refreshData}
            disabled={loading || loadingStats}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards - Overall Totals (Not Filter-Dependent) */}
      {/* Stats show overall totals for all appointments, not filtered counts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Today's Appointments
                  </Typography>
                  {loadingStats ? <CircularProgress size={20} /> : <CalendarToday color="primary" />}
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {statsValues.today}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                  Scheduled for today
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                  {loadingStats ? <CircularProgress size={20} /> : <Pending color="warning" />}
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {statsValues.pending}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                  {statsValues.pending === 0 ? 'No pending appointments' : 'Total pending'}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                  {loadingStats ? <CircularProgress size={20} /> : <CheckCircle color="success" />}
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {statsValues.completed}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                  {statsValues.completed === 0 ? 'No completed appointments' : 'Total completed'}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Cancelled
                  </Typography>
                  {loadingStats ? <CircularProgress size={20} /> : <Cancel color="error" />}
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {statsValues.cancelled}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                  {statsValues.cancelled === 0 ? 'No cancelled appointments' : 'Total cancelled'}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by patient name, ID, or phone..."
              value={searchInputValue}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              value={filterStatus}
              onChange={handleStatusFilterChange}
              variant="outlined"
            >
              <MenuItem value="All">All Statuses</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Confirmed">Confirmed</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Appointments Table */}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
            {loading && appointments.length === 0 ? (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="400px" gap={2}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Loading appointments...
                </Typography>
              </Box>
            ) : error && appointments.length === 0 ? (
              <Alert 
                severity="error" 
                sx={{ m: 2 }}
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => {
                      dispatch(clearError());
                      fetchAppointmentsWithRetry();
                    }}
                    disabled={retryCount >= 3}
                  >
                    {retryCount >= 3 ? 'Max Retries' : 'Retry'}
                  </Button>
                }
              >
                {typeof error === 'string' ? error : error?.detail || error?.message || 'Failed to load appointments. Please try again.'}
                {retryCount > 0 && retryCount < 3 && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Retrying... ({retryCount}/3)
                  </Typography>
                )}
              </Alert>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Patient</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Alert severity="error">
                          {typeof error === 'string' ? error : error?.detail || 'Failed to load appointments'}
                        </Alert>
                      </TableCell>
                    </TableRow>
                  ) : appointments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <CalendarToday sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3 }} />
                          <Typography variant="h6" color="text.secondary">
                            No appointments found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {searchTerm || filterStatus !== 'All' 
                              ? 'Try adjusting your search or filters'
                              : 'You don\'t have any appointments yet'}
                          </Typography>
                          <Button 
                            variant="outlined" 
                            onClick={() => dispatch(fetchAppointments({ page, search: searchTerm, status: filterStatus }))}
                          >
                            Refresh
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    appointments.map((appt) => (
                    <TableRow
                      key={appt.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar
                            src={appt.patient?.profile_image || appt.patient_avatar}
                            alt={appt.patient?.name || appt.patient_name || 'Patient'}
                            sx={{ width: 40, height: 40 }}
                          >
                            {(appt.patient?.name || appt.patient_name || 'P').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {appt.patient?.name || appt.patient_name || 'Unknown Patient'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {appt.patient?.phone_number || appt.patient_phone || 'N/A'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday fontSize="small" color="action" />
                            {appt.formatted_date || (appt.scheduled_date ? format(new Date(appt.scheduled_date), 'MMM dd, yyyy') : 'N/A')}
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTime fontSize="small" color="action" />
                            {appt.formatted_time || appt.scheduled_time || 'N/A'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getConsultationTypeIcon(appt.consultation_type || appt.type || 'Consultation')}
                          label={appt.consultation_type || appt.type || 'Consultation'}
                          size="small"
                          color={(appt.consultation_type || appt.type) === "Video Call" ? "info" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          icon={getStatusIcon(appt.status)}
                          label={appt.status}
                          status={appt.status}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                          <AppointmentStatusManager
                            appointment={appt}
                            userRole="doctor"
                            onStatusChange={refreshData}
                          />
                          <AppointmentActions
                            appointment={appt}
                            canReschedule={appt.can_reschedule !== false}
                            canCancel={appt.can_cancel !== false}
                            canConfirm={appt.can_confirm === true || appt.status === 'Pending'}
                            onUpdate={refreshData}
                          />
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPopup(appt)}
                              sx={{
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                },
                              }}
                            >
                              <MoreVert />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </motion.div>
      </AnimatePresence>

      {/* Pagination */}
      {count > 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={2} flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            {count > itemsPerPage ? (
              <>
                Showing {((page - 1) * itemsPerPage) + 1} - {Math.min(page * itemsPerPage, count)} of {count} {searchTerm || filterStatus !== 'All' ? 'filtered ' : ''}appointments
                {statsValues.total > 0 && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                    (Total: {statsValues.total} appointments)
                  </Typography>
                )}
              </>
            ) : (
              <>
                Showing {count} {count === 1 ? 'appointment' : 'appointments'}
                {statsValues.total > 0 && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                    (Total: {statsValues.total} appointments)
                  </Typography>
                )}
              </>
            )}
          </Typography>
          {count > itemsPerPage && (
            <Pagination
              count={Math.ceil(count / itemsPerPage)}
              page={page}
              onChange={(e, value) => {
                setPage(value);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              color="primary"
              shape="rounded"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
            />
          )}
        </Box>
      )}

      {/* Appointment Details Popup */}
      {selectedAppointment && (
        <AppointmentPopup
          open={openPopup}
          handleClose={handleClosePopup}
          appointment={selectedAppointment}
          onUpdate={refreshData}
        />
      )}

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DoctorAppointments;